import type { Integraciones } from '../api/modelos'
import {
  IconoLogistica, IconoConversaciones, IconoLeads, IconoAvisos, IconoAgendamiento, IconoCalendario, IconoConfirmar, IconoRecalculo, IconoIncidencias, IconoAuditoria, IconoGE,
  IconoLogisticaSolido, IconoConversacionesSolido, IconoLeadsSolido, IconoAvisosSolido,
  IconoAgendamientoSolido, IconoCalendarioSolido, IconoConfirmarSolido, IconoRecalculoSolido, IconoIncidenciasSolido, IconoAuditoriaSolido, IconoGESolido,
  IconoClientes, IconoCatalogo, IconoAlbaranes, IconoRutaDia, IconoAvisoSemanal, IconoUsuarios, IconoAjustes, IconoWorkflows,
  IconoRuta, IconoCicloSemanal, IconoGrupoClientes, IconoGrupoCatalogo, IconoSistemaGrupo,
} from './iconos'

export interface Destino {
  ruta: string
  etiqueta: string
  /**
   * Rotulo corto, de diez caracteres como mucho, para sitios estrechos: la
   * celda del inicio en la barra inferior de movil. Los grupos llevan el suyo
   * (`Grupo.etiquetaMovil`); dentro de las hojas cabe el largo.
   */
  etiquetaMovil: string
  Icono: React.ComponentType<{ className?: string }>
  IconoActivo: React.ComponentType<{ className?: string }>
  /** Solo para administradores (spec 002: Usuarios y Ajustes). */
  soloAdmin?: boolean
  /** Solo si la integración está configurada (RF-48: Workflows necesita n8n). */
  integracion?: 'n8n'
}

export const DESTINOS: Destino[] = [
  { ruta: '/logistica', etiqueta: 'Logística', etiquetaMovil: 'Logística', Icono: IconoLogistica, IconoActivo: IconoLogisticaSolido },
  { ruta: '/clientes', etiqueta: 'Clientes', etiquetaMovil: 'Clientes', Icono: IconoClientes, IconoActivo: IconoClientes },
  { ruta: '/catalogo', etiqueta: 'Catálogo', etiquetaMovil: 'Catálogo', Icono: IconoCatalogo, IconoActivo: IconoCatalogo },
  { ruta: '/conversaciones/clientes', etiqueta: 'Conversaciones', etiquetaMovil: 'Mensajes', Icono: IconoConversaciones, IconoActivo: IconoConversacionesSolido },
  { ruta: '/conversaciones/leads', etiqueta: 'Leads', etiquetaMovil: 'Leads', Icono: IconoLeads, IconoActivo: IconoLeadsSolido },
  { ruta: '/avisos', etiqueta: 'Avisos', etiquetaMovil: 'Avisos', Icono: IconoAvisos, IconoActivo: IconoAvisosSolido },
  { ruta: '/aviso-semanal', etiqueta: 'Aviso semanal', etiquetaMovil: 'Aviso sem.', Icono: IconoAvisoSemanal, IconoActivo: IconoAvisoSemanal },
  { ruta: '/confirmar', etiqueta: 'Confirmar', etiquetaMovil: 'Confirmar', Icono: IconoConfirmar, IconoActivo: IconoConfirmarSolido },
  { ruta: '/incidencias', etiqueta: 'Incidencias', etiquetaMovil: 'Incid.', Icono: IconoIncidencias, IconoActivo: IconoIncidenciasSolido },
  { ruta: '/agendamiento', etiqueta: 'Agendamiento', etiquetaMovil: 'Agenda', Icono: IconoAgendamiento, IconoActivo: IconoAgendamientoSolido },
  { ruta: '/albaranes', etiqueta: 'Albaranes', etiquetaMovil: 'Albaranes', Icono: IconoAlbaranes, IconoActivo: IconoAlbaranes },
  { ruta: '/ruta-del-dia', etiqueta: 'Ruta del día', etiquetaMovil: 'Ruta día', Icono: IconoRutaDia, IconoActivo: IconoRutaDia },
  { ruta: '/grandes-empresas', etiqueta: 'Grandes Empresas', etiquetaMovil: 'GE', Icono: IconoGE, IconoActivo: IconoGESolido },
  { ruta: '/calendario', etiqueta: 'Calendario', etiquetaMovil: 'Calend.', Icono: IconoCalendario, IconoActivo: IconoCalendarioSolido },
  { ruta: '/auditoria', etiqueta: 'Auditoría', etiquetaMovil: 'Audit.', Icono: IconoAuditoria, IconoActivo: IconoAuditoriaSolido },
  { ruta: '/recalculo', etiqueta: 'Recálculo', etiquetaMovil: 'Recálculo', Icono: IconoRecalculo, IconoActivo: IconoRecalculoSolido },
  { ruta: '/workflows', etiqueta: 'Workflows', etiquetaMovil: 'Workflows', Icono: IconoWorkflows, IconoActivo: IconoWorkflows, integracion: 'n8n' },
  { ruta: '/usuarios', etiqueta: 'Usuarios', etiquetaMovil: 'Usuarios', Icono: IconoUsuarios, IconoActivo: IconoUsuarios, soloAdmin: true },
  { ruta: '/ajustes', etiqueta: 'Ajustes', etiquetaMovil: 'Ajustes', Icono: IconoAjustes, IconoActivo: IconoAjustes, soloAdmin: true },
]

/**
 * Destinos que ve el usuario (RF-48). Sin saber aún las integraciones (cargando
 * o error), lo que depende de una integración se oculta: mejor no enseñar una
 * pantalla que va a decir «falta configurar».
 */
export function destinosVisibles(
  destinos: readonly Destino[],
  { admin, integraciones }: { admin: boolean; integraciones?: Integraciones | null },
): Destino[] {
  return destinos.filter((destino) => {
    if (destino.soloAdmin && !admin) return false
    if (destino.integracion === 'n8n' && !integraciones?.n8n?.configurado) return false
    return true
  })
}

/** El inicio va suelto, encima de los grupos, en la lateral y en la barra movil. */
export const INICIO: Destino = DESTINOS[0]

export type IdGrupo = 'operacion' | 'semana' | 'clientes' | 'catalogo' | 'sistema'

/**
 * Menu agrupado por tarea (variante B del mockup `sketches/menu-agrupado`).
 * Cada pantalla esta en el grupo de lo que hace, no de como se llama:
 * Incidencias nace de las conversaciones con clientes y va con Clientes.
 * «Sistema» no es solo de admin: Auditoria la ve todo el equipo; Usuarios y
 * Ajustes siguen filtrados por `soloAdmin` y Workflows por `integracion`.
 */
export interface Grupo {
  id: IdGrupo
  etiqueta: string
  /** Rotulo de la barra inferior de movil: diez caracteres como mucho. */
  etiquetaMovil: string
  Icono: React.ComponentType<{ className?: string }>
  /** Rutas de `DESTINOS`, en el orden en que salen en el panel. */
  rutas: string[]
}

export const GRUPOS: Grupo[] = [
  { id: 'operacion', etiqueta: 'Operación diaria', etiquetaMovil: 'Operación', Icono: IconoRuta, rutas: ['/agendamiento', '/ruta-del-dia', '/calendario', '/albaranes'] },
  { id: 'semana', etiqueta: 'Ciclo semanal', etiquetaMovil: 'Semana', Icono: IconoCicloSemanal, rutas: ['/aviso-semanal', '/avisos', '/confirmar'] },
  { id: 'clientes', etiqueta: 'Clientes', etiquetaMovil: 'Clientes', Icono: IconoGrupoClientes, rutas: ['/clientes', '/grandes-empresas', '/conversaciones/clientes', '/conversaciones/leads', '/incidencias'] },
  { id: 'catalogo', etiqueta: 'Catálogo', etiquetaMovil: 'Catálogo', Icono: IconoGrupoCatalogo, rutas: ['/catalogo', '/recalculo'] },
  { id: 'sistema', etiqueta: 'Sistema', etiquetaMovil: 'Sistema', Icono: IconoSistemaGrupo, rutas: ['/auditoria', '/workflows', '/usuarios', '/ajustes'] },
]

/**
 * Grupos con boton propio en la barra inferior de movil, entre el inicio y
 * «Más». Cinco celdas a 375 px son 75 px cada una: rotulos de diez caracteres
 * y objetivos tactiles holgados. El resto de grupos y la cuenta van en «Más».
 */
export const GRUPOS_BARRA_MOVIL: readonly IdGrupo[] = ['operacion', 'semana', 'clientes']

export interface GrupoVisible extends Omit<Grupo, 'rutas'> {
  destinos: Destino[]
}

/**
 * Agrupa los destinos que ya pasaron `destinosVisibles`. Un grupo que se queda
 * sin entradas (rol o integracion) desaparece entero: un desplegable vacio es
 * peor que no tenerlo.
 */
export function gruposVisibles(visibles: readonly Destino[]): GrupoVisible[] {
  const porRuta = new Map(visibles.map((d) => [d.ruta, d]))
  return GRUPOS.map(({ rutas, ...grupo }) => ({
    ...grupo,
    destinos: rutas.flatMap((ruta) => porRuta.get(ruta) ?? []),
  })).filter((grupo) => grupo.destinos.length > 0)
}

/** Ruta igual o subruta (`/clientes/42`), nunca un prefijo parcial (`/avisos-x`). */
export function rutaCoincide(pathname: string, ruta: string): boolean {
  return pathname === ruta || pathname.startsWith(`${ruta}/`)
}

/** Grupo que contiene la pantalla abierta; el inicio no es de ninguno. */
export function grupoDeRuta(pathname: string, grupos: readonly GrupoVisible[]): GrupoVisible | undefined {
  return grupos.find((grupo) => grupo.destinos.some((d) => rutaCoincide(pathname, d.ruta)))
}
